/**
 * ETIQUETAS PROPIAS DE UNA ORGANIZACIÓN PARA SUS CHATS · punto 11 (4-nov-2026).
 *
 * Pedido: crear, editar y borrar, sólo owner y admin; asignar y quitar, cualquier miembro; máximo 30 por
 * organización; por ahora sólo en la bandeja (el Dashboard, después).
 *
 * DÓNDE VIVE CADA COSA
 *   · El catálogo: UN documento, `organizations/{orgId}/etiquetas/catalogo`, `{ lista: [{ id, nombre, color }] }`.
 *     Lo escribe sólo el callable `gestionarEtiquetasDeChat` (las reglas lo niegan al cliente) en una transacción,
 *     así que el tope de 30 y los nombres repetidos se comprueban sobre lo que hay de verdad.
 *   · La asignación: `etiquetas: [id, …]` en el documento del chat, escrita desde la bandeja con `arrayUnion` /
 *     `arrayRemove` (como el estado del chat). Las reglas la limitan a una lista de hasta 30.
 *
 * QUIÉN GESTIONA: por ROL, no por un permiso de la matriz. Los permisos están copiados en cada documento de
 * miembro y se editan por organización (`roleConfigs`, pestaña Roles, servidos por `server/`): un permiso nuevo
 * no existiría en los miembros de hoy y obligaría a rellenarlos en todas las organizaciones y a desplegar
 * `server/`. Si un día se quiere configurable en Roles, es ese trabajo.
 *
 * BORRAR una etiqueta la quita del catálogo y después de los chats que la llevan. Lo que se ve no depende de esa
 * segunda mitad: `etiquetasDelChat` sólo devuelve las que siguen en el catálogo (un id huérfano no se pinta ni
 * filtra), y los ids no se reutilizan.
 *
 * DOS COPIAS BYTE-IDÉNTICAS: `functions/utils/etiquetasDeChat.cjs` (el callable) y `src/utils/etiquetasDeChat.js`
 * (la bandeja); `functions/scripts/check-shared-module-parity.js` las compara. Si divergieran, la bandeja dejaría
 * pedir lo que el servidor rechaza, o al revés. PURO: sin Firestore ni red.
 */

const MAX_ETIQUETAS = 30;
const MAX_NOMBRE = 24;
const ROLES_QUE_GESTIONAN = ['owner', 'admin'];

/** Los colores, por nombre: el documento guarda el nombre y la bandeja pinta el valor. */
const COLORES = {
  gris: '#64748b',
  rojo: '#dc2626',
  naranja: '#ea580c',
  amarillo: '#ca8a04',
  verde: '#16a34a',
  turquesa: '#0d9488',
  azul: '#2563eb',
  morado: '#7c3aed',
  rosa: '#db2777',
};
const COLOR_POR_DEFECTO = 'azul';

/** Espacios de los lados fuera y los de dentro juntados en uno. */
function normalizarNombre(nombre) {
  return typeof nombre === 'string' ? nombre.trim().replace(/\s+/g, ' ') : '';
}

/** Para comparar nombres: sin mayúsculas ni acentos («Boda» y «boda» son la misma etiqueta). */
function claveDeNombre(nombre) {
  return normalizarNombre(nombre).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

function errorDeNombre(nombre) {
  if (!nombre) return 'nombre-vacio';
  if (nombre.length > MAX_NOMBRE) return 'nombre-largo';
  return null;
}

const listaDe = (lista) => (Array.isArray(lista) ? lista.filter((e) => e && typeof e.id === 'string') : []);

/**
 * El catálogo después de un pedido, o el motivo por el que no.
 * @param {Array} lista  el catálogo actual
 * @param {{ accion: 'crear'|'editar'|'borrar', id?: string, nombre?: string, color?: string }} pedido
 * @param {{ nuevoId?: () => string }} [opciones]  para `crear` (el servidor genera el id)
 * @returns {{ ok: true, lista: Array, etiqueta: object } | { ok: false, error: string }}
 */
function cambiarCatalogo(lista, pedido, { nuevoId } = {}) {
  const actual = listaDe(lista);
  const accion = pedido && pedido.accion;
  const repetida = (nombre, salvoId) => actual.some((e) => e.id !== salvoId && claveDeNombre(e.nombre) === claveDeNombre(nombre));

  if (accion === 'crear') {
    if (actual.length >= MAX_ETIQUETAS) return { ok: false, error: 'tope' };
    const nombre = normalizarNombre(pedido.nombre);
    const e = errorDeNombre(nombre);
    if (e) return { ok: false, error: e };
    const color = pedido.color == null ? COLOR_POR_DEFECTO : pedido.color;
    if (!Object.prototype.hasOwnProperty.call(COLORES, color)) return { ok: false, error: 'color-invalido' };
    if (repetida(nombre)) return { ok: false, error: 'repetida' };
    const id = typeof nuevoId === 'function' ? nuevoId() : null;
    if (typeof id !== 'string' || !id || actual.some((x) => x.id === id)) return { ok: false, error: 'id-invalido' };
    const etiqueta = { id, nombre, color };
    return { ok: true, lista: [...actual, etiqueta], etiqueta };
  }

  if (accion === 'editar' || accion === 'borrar') {
    const previa = actual.find((x) => x.id === (pedido && pedido.id));
    if (!previa) return { ok: false, error: 'no-existe' };
    if (accion === 'borrar') return { ok: true, lista: actual.filter((x) => x.id !== previa.id), etiqueta: previa };
    const nombre = pedido.nombre == null ? previa.nombre : normalizarNombre(pedido.nombre);
    const e = errorDeNombre(nombre);
    if (e) return { ok: false, error: e };
    const color = pedido.color == null ? previa.color : pedido.color;
    if (!Object.prototype.hasOwnProperty.call(COLORES, color)) return { ok: false, error: 'color-invalido' };
    if (repetida(nombre, previa.id)) return { ok: false, error: 'repetida' };
    const etiqueta = { ...previa, nombre, color };
    return { ok: true, lista: actual.map((x) => (x.id === previa.id ? etiqueta : x)), etiqueta };
  }

  return { ok: false, error: 'accion-invalida' };
}

/**
 * ¿Este ROL crea, edita y borra? owner y admin. Recibe el rol y no el miembro: en la bandeja el miembro llega sin
 * `status` (`OrganizationContext` sólo copia rol, permisos y alta); el callable exige además `status === 'active'`.
 */
function puedeGestionarEtiquetas(rol) {
  return ROLES_QUE_GESTIONAN.includes(rol);
}

/** Las etiquetas del chat que siguen en el catálogo, en el orden del catálogo. */
function etiquetasDelChat(chat, catalogo) {
  const ids = Array.isArray(chat && chat.etiquetas) ? chat.etiquetas : [];
  if (!ids.length) return [];
  return listaDe(catalogo).filter((e) => ids.includes(e.id));
}

/** Para el filtro de la bandeja: `'todas'` no filtra; un id deja los chats que la llevan. */
function cumpleFiltroDeEtiqueta(chat, filtro) {
  if (!filtro || filtro === 'todas') return true;
  return Array.isArray(chat && chat.etiquetas) && chat.etiquetas.includes(filtro);
}

module.exports = {
  MAX_ETIQUETAS,
  MAX_NOMBRE,
  ROLES_QUE_GESTIONAN,
  COLORES,
  COLOR_POR_DEFECTO,
  normalizarNombre,
  claveDeNombre,
  cambiarCatalogo,
  puedeGestionarEtiquetas,
  etiquetasDelChat,
  cumpleFiltroDeEtiqueta,
};
